package com.arena_web.core.viewmodel

import com.arena_web.data.dto.*
import com.arena_web.domain.repositories.CardRepository
import com.arena_web.domain.repositories.EventRepository
import com.arena_web.domain.repositories.FollowRepository
import com.arena_web.domain.repositories.TeamRepository
import com.arena_web.domain.repositories.UserRepository
import com.arena_web.utils.Storage
import com.arena_web.utils.StorageKeys
import kotlinx.coroutines.async
import kotlinx.coroutines.awaitAll
import kotlinx.coroutines.coroutineScope
import kotlinx.coroutines.flow.MutableStateFlow
import kotlinx.coroutines.flow.StateFlow
import kotlinx.coroutines.flow.asStateFlow
import kotlinx.coroutines.flow.update
import kotlinx.coroutines.launch

/** Server-side page size for the "Recent events" section. */
const val MATCH_HISTORY_PAGE_SIZE = 2

data class ProfileUiState(
    val isLoading: Boolean = true,
    val user: UserDto? = null,
    val sportProfile: SportProfileDto? = null,
    /** Cross-sport aggregate populated when the user picks "All" in the
     *  header sport selector. `sportProfile` stays `null` in that case —
     *  the UI reads `aggregateRating` for the FIFA OVR badge and hides
     *  the radar / stats / position blocks (sport-specific by design). */
    val aggregateRating: SportProfileAggregateDto? = null,
    val statsCharts: Map<String, StatsChartDto> = emptyMap(),
    val goalsChartPeriod: String = "month",
    val assistsChartPeriod: String = "month",
    val team: TeamWithMembersDto? = null,
    val teamMatches: List<TeamMatchDto> = emptyList(),
    val teamChart: StatsChartDto? = null,
    val teamChartPeriod: String = "month",
    val favoriteMedia: List<EventMediaDto> = emptyList(),
    /** Earned awards, newest first — drives the Achievements Awards tab. */
    val awards: List<UserAwardDto> = emptyList(),
    /** Cross-sport completed events the user took part in — feeds the
     *  "Recent events" section. Each `EventDto` carries `result` (W/L/D),
     *  `teamScores` (with team logo/color), and the basic event metadata
     *  the section renders. Server-paginated: this list holds only the
     *  current page (2 items); navigation arrows in the UI trigger a
     *  fetch for the next/previous page via `loadMatchHistoryPage`. */
    val matchHistory: List<EventDto> = emptyList(),
    val matchHistoryPage: Int = 0,
    val matchHistoryTotal: Long = 0,
    /** Flipped on while a `/events?participantId=…` page is in flight —
     *  drives the inline loader inside `RecentMatchesSection`. Decoupled
     *  from the top-level `isLoading` so the section can show its own
     *  spinner without blocking the rest of the profile from rendering. */
    val matchHistoryLoading: Boolean = false,
    /** Full profile-card gallery in display order. First entry is the main
     *  card (mirrors `user.mainProfileCard`). Loaded on profile open and
     *  refetched after generate-confirm / reorder / delete. */
    val profileCards: List<ProfileCardDto> = emptyList(),
    val profileCardsLoading: Boolean = false,
    /** Full template catalog from `getMyCards()` — each entry carries an
     *  `isOwned` flag. Locked entries render with a lock badge in the
     *  Bring-Your-Card-to-Life popup and trigger the purchase flow on click. */
    val ownedTemplates: List<UserCardDto> = emptyList(),
    /** Template the user has clicked to buy — drives [PurchaseCardModal]. */
    val pendingPurchase: UserCardDto? = null,
    val purchasing: Boolean = false,
    val purchaseError: String? = null,
    val followers: List<FollowUserDto> = emptyList(),
    val following: List<FollowUserDto> = emptyList(),
    val followersCount: Long? = null,
    val followingCount: Long? = null,
    val error: String? = null,
    // Follow relation (for public profiles)
    val isFollowing: Boolean = false,
    val isFollowedBy: Boolean = false,
    val followActionLoading: Boolean = false,
    // Follow popups
    val showFollowersDialog: Boolean = false,
    val showFollowingDialog: Boolean = false,
    val followersListLoading: Boolean = false,
    val followingListLoading: Boolean = false,
    val unfollowConfirmTarget: FollowUserDto? = null,
    val removeFollowerConfirmTarget: FollowUserDto? = null,
    /** IDs of followers that were just removed from this session — show as "Deleted" pill. */
    val deletedFollowerIds: Set<String> = emptySet(),
    /** IDs that were unfollowed in the Following dialog — toggle button to "Follow" state. */
    val unfollowedInSessionIds: Set<String> = emptySet()
) {
    // Profile self-description is now part of UserDto — derive the view state on demand
    // so the source of truth stays the server response.
    val languages: List<String> get() = user?.languages ?: emptyList()
    val interestSports: Set<String> get() = user?.interestSports?.toSet() ?: emptySet()
    val interestActivities: Set<String> get() = user?.interestActivities?.toSet() ?: emptySet()
    val interestLifestyle: Set<String> get() = user?.interestLifestyle?.toSet() ?: emptySet()
    val interestNotes: Map<String, String> get() = user?.interestNotes ?: emptyMap()
}

class ProfileViewModel(
    private val userRepository: UserRepository,
    private val teamRepository: TeamRepository? = null,
    private val followRepository: FollowRepository? = null,
    private val cardRepository: CardRepository? = null,
    private val eventRepository: EventRepository? = null,
) : BaseViewModel() {

    private val _uiState = MutableStateFlow(ProfileUiState(
        goalsChartPeriod = Storage.get(StorageKeys.CHART_GOALS_PERIOD) ?: "month",
        assistsChartPeriod = Storage.get(StorageKeys.CHART_ASSISTS_PERIOD) ?: "month"
    ))
    val uiState: StateFlow<ProfileUiState> = _uiState.asStateFlow()

    override fun onCreate() {
        super.onCreate()
        loadProfile()
    }

    fun loadProfile() {
        viewModelScope.launch {
            _uiState.update { it.copy(isLoading = true, error = null, statsCharts = emptyMap()) }

            // userId / sport selection come from Storage so every request below
            // — including getMe — fires concurrently. The full UserDto from
            // getMe still drops into state asynchronously for header rendering.
            val cachedUserId = Storage.get(StorageKeys.USER_ID)
            val sportId = Storage.get(StorageKeys.SELECTED_SPORT_ID)
            val sportName = Storage.get(StorageKeys.SELECTED_SPORT_NAME)?.lowercase()
            val hasSport = !sportId.isNullOrBlank()

            coroutineScope {
                val jobs = mutableListOf<kotlinx.coroutines.Deferred<*>>()

                jobs += async {
                    userRepository.getMe()
                        .onSuccess { u -> _uiState.update { it.copy(user = u) } }
                        .onFailure { e -> _uiState.update { it.copy(error = e.message) } }
                }

                if (hasSport) {
                    jobs += async {
                        userRepository.getMySportProfile(sportId!!)
                            .onSuccess { profile -> _uiState.update { it.copy(sportProfile = profile, aggregateRating = null) } }
                            .onFailure { e -> logger.debug("Sport profile not available: ${e.message}") }
                    }
                    if (sportName != "other") {
                        jobs += async { loadStatsChartIfNeeded(_uiState.value.goalsChartPeriod) }
                        val assistsPeriod = _uiState.value.assistsChartPeriod
                        if (assistsPeriod != _uiState.value.goalsChartPeriod) {
                            jobs += async { loadStatsChartIfNeeded(assistsPeriod) }
                        }
                    }
                } else {
                    // "All" pseudo-sport: fetch the cross-sport overall-rating
                    // average. Single scalar — radar / stats / position
                    // are sport-specific and stay hidden in the UI.
                    jobs += async {
                        userRepository.getMySportProfileAggregate()
                            .onSuccess { agg -> _uiState.update { it.copy(sportProfile = null, aggregateRating = agg) } }
                            .onFailure { e -> logger.debug("Aggregate rating not available: ${e.message}") }
                    }
                }

                cachedUserId?.let { uid ->
                    jobs += async {
                        userRepository.getUserFavoriteMedia(uid)
                            .onSuccess { media -> _uiState.update { it.copy(favoriteMedia = media) } }
                            .onFailure { e -> logger.debug("Failed to load favorite media: ${e.message}") }
                    }
                }

                jobs += async {
                    userRepository.getMyAwards()
                        .onSuccess { awards -> _uiState.update { it.copy(awards = awards) } }
                        .onFailure { e -> logger.debug("Failed to load awards: ${e.message}") }
                }

                // Recent events are intentionally NOT joined to `jobs.awaitAll`
                // below — the SQL behind `/events?participantId=…` is the
                // slowest call in `loadProfile`, so we fire it on its own
                // launch with a section-scoped `matchHistoryLoading` flag.
                // The rest of the profile drops `isLoading=false` immediately,
                // and the section renders its inline spinner until the page
                // resolves.
                cachedUserId?.let { uid -> fetchMatchHistoryPage(uid, page = 0) }

                // Follow counts only — the full follower / following lists
                // are deferred to dialog-open via `reloadFollowers` /
                // `reloadFollowing`. Profile header just renders the totals.
                cachedUserId?.let { uid ->
                    followRepository?.let { repo ->
                        jobs += async {
                            repo.getCounts(uid)
                                .onSuccess { counts ->
                                    _uiState.update {
                                        it.copy(
                                            followersCount = counts.followersCount,
                                            followingCount = counts.followingCount,
                                        )
                                    }
                                }
                                .onFailure { e -> logger.debug("Failed to load follow counts: ${e.message}") }
                        }
                    }
                }

                jobs += async { loadProfileCardsAwait() }
                jobs += async { loadOwnedTemplatesAwait() }

                jobs.awaitAll()
            }

            _uiState.update { it.copy(isLoading = false) }
        }
    }

    /** Fetch a specific page of the user's completed events. UI navigation
     *  arrows call this — the section flips its loader and swaps to the
     *  requested page index immediately, then the list resolves async. */
    fun loadMatchHistoryPage(page: Int) {
        val uid = Storage.get(StorageKeys.USER_ID) ?: return
        if (page < 0) return
        fetchMatchHistoryPage(uid, page)
    }

    private fun fetchMatchHistoryPage(userId: String, page: Int) {
        val repo = eventRepository ?: return
        // Bump the page index + loader BEFORE the network call so the
        // counter and disabled-arrow state in the section reflect the
        // user's intent the moment they tap the arrow.
        _uiState.update { it.copy(matchHistoryPage = page, matchHistoryLoading = true) }
        viewModelScope.launch {
            repo.getEvents(matchHistoryParams(userId, page = page))
                .onSuccess { result ->
                    _uiState.update {
                        it.copy(
                            matchHistory = result.events,
                            matchHistoryPage = result.page,
                            matchHistoryTotal = result.total,
                            matchHistoryLoading = false,
                        )
                    }
                }
                .onFailure { e ->
                    logger.debug("Failed to load recent events page $page: ${e.message}")
                    _uiState.update { it.copy(matchHistoryLoading = false) }
                }
        }
    }

    private fun matchHistoryParams(userId: String, page: Int): Map<String, String> =
        mapOf(
            "participantId" to userId,
            "status" to "COMPLETED",
            "page" to page.toString(),
            "size" to MATCH_HISTORY_PAGE_SIZE.toString(),
        )

    /** Refetch sport-scoped data only — the sport profile (drives back-side
     *  rating + radar) and the two stat charts. Called from the page when
     *  the user switches active sport in the header, so the rest of the
     *  profile (user, match history, follows, cards) stays put instead of
     *  triggering a full `loadProfile` re-flow. */
    fun reloadSportData() {
        viewModelScope.launch {
            // Charts are keyed by period only — without this reset the
            // `containsKey(period)` short-circuit in `loadStatsChartIfNeeded`
            // would keep showing the previous sport's data.
            _uiState.update { it.copy(statsCharts = emptyMap()) }

            val sportId = Storage.get(StorageKeys.SELECTED_SPORT_ID)
            val sportName = Storage.get(StorageKeys.SELECTED_SPORT_NAME)?.lowercase()
            if (sportId.isNullOrBlank()) {
                // "All" pseudo-sport — drop the per-sport profile and pull
                // the cross-sport average (single scalar). UI hides radar /
                // stats / position blocks since those are sport-specific.
                _uiState.update { it.copy(sportProfile = null) }
                userRepository.getMySportProfileAggregate()
                    .onSuccess { agg -> _uiState.update { it.copy(aggregateRating = agg) } }
                    .onFailure { e -> logger.debug("Aggregate rating not available: ${e.message}") }
                return@launch
            }

            coroutineScope {
                val jobs = mutableListOf<kotlinx.coroutines.Deferred<*>>()
                jobs += async {
                    userRepository.getMySportProfile(sportId)
                        .onSuccess { profile -> _uiState.update { it.copy(sportProfile = profile, aggregateRating = null) } }
                        .onFailure { e -> logger.debug("Sport profile not available: ${e.message}") }
                }
                if (sportName != "other") {
                    jobs += async { loadStatsChartIfNeeded(_uiState.value.goalsChartPeriod) }
                    val assistsPeriod = _uiState.value.assistsChartPeriod
                    if (assistsPeriod != _uiState.value.goalsChartPeriod) {
                        jobs += async { loadStatsChartIfNeeded(assistsPeriod) }
                    }
                }
                jobs.awaitAll()
            }
        }
    }

    /** Refresh the profile-card gallery — call after avatar confirm / reorder / delete. */
    fun loadProfileCards() {
        cardRepository ?: return
        viewModelScope.launch { loadProfileCardsAwait() }
    }

    private suspend fun loadProfileCardsAwait() {
        val repo = cardRepository ?: return
        _uiState.update { it.copy(profileCardsLoading = true) }
        repo.getMyProfileCards()
            .onSuccess { cards ->
                _uiState.update {
                    it.copy(
                        profileCards = cards.sortedBy { c -> c.displayOrder },
                        profileCardsLoading = false,
                    )
                }
            }
            .onFailure { e ->
                logger.debug("Failed to load profile cards: ${e.message}")
                _uiState.update { it.copy(profileCardsLoading = false) }
            }
    }

    /** Persist a new order. `orderedIds` must contain every profile card. */
    fun reorderProfileCards(orderedIds: List<String>) {
        val repo = cardRepository ?: return
        // Optimistic: reorder locally so the UI updates immediately.
        _uiState.update { state ->
            val byId = state.profileCards.associateBy { it.id }
            val newOrder = orderedIds.mapIndexedNotNull { idx, id ->
                byId[id]?.copy(displayOrder = idx)
            }
            state.copy(profileCards = newOrder)
        }
        viewModelScope.launch {
            repo.reorderProfileCards(orderedIds)
                .onSuccess { cards ->
                    _uiState.update {
                        it.copy(profileCards = cards.sortedBy { c -> c.displayOrder })
                    }
                    // Reload user so `user.mainProfileCard` reflects the new head.
                    refreshMainProfileCard()
                }
                .onFailure { e ->
                    logger.debug("Reorder failed: ${e.message}")
                    loadProfileCards()
                }
        }
    }

    fun deleteProfileCard(profileCardId: String) {
        val repo = cardRepository ?: return
        viewModelScope.launch {
            repo.deleteProfileCard(profileCardId)
                .onSuccess { cards ->
                    _uiState.update {
                        it.copy(profileCards = cards.sortedBy { c -> c.displayOrder })
                    }
                    refreshMainProfileCard()
                }
                .onFailure { e -> logger.debug("Delete profile card failed: ${e.message}") }
        }
    }

    /** Clears a card's generated photo but keeps the card record. */
    fun clearProfileCardAvatar(profileCardId: String) {
        val repo = cardRepository ?: return
        viewModelScope.launch {
            repo.clearProfileCardAvatar(profileCardId)
                .onSuccess { cards ->
                    _uiState.update {
                        it.copy(profileCards = cards.sortedBy { c -> c.displayOrder })
                    }
                    refreshMainProfileCard()
                }
                .onFailure { e -> logger.debug("Clear profile card avatar failed: ${e.message}") }
        }
    }

    /** Refresh the owned-template list — call after a card-shop purchase. */
    fun loadOwnedTemplates() {
        cardRepository ?: return
        viewModelScope.launch { loadOwnedTemplatesAwait() }
    }

    private suspend fun loadOwnedTemplatesAwait() {
        val repo = cardRepository ?: return
        repo.getMyCards()
            .onSuccess { cards -> _uiState.update { it.copy(ownedTemplates = cards) } }
            .onFailure { e -> logger.debug("Failed to load owned templates: ${e.message}") }
    }

    fun requestPurchase(template: UserCardDto) {
        _uiState.update { it.copy(pendingPurchase = template, purchaseError = null) }
    }

    fun cancelPurchase() {
        if (_uiState.value.purchasing) return
        _uiState.update { it.copy(pendingPurchase = null, purchaseError = null) }
    }

    fun confirmPurchase() {
        val repo = cardRepository ?: return
        val target = _uiState.value.pendingPurchase ?: return
        viewModelScope.launch {
            _uiState.update { it.copy(purchasing = true, purchaseError = null) }
            repo.purchaseCard(PurchaseCardRequest(target.templateId))
                .onSuccess { cards ->
                    _uiState.update {
                        it.copy(
                            ownedTemplates = cards,
                            purchasing = false,
                            pendingPurchase = null,
                            purchaseError = null,
                        )
                    }
                }
                .onFailure { e ->
                    _uiState.update { it.copy(purchasing = false, purchaseError = e.message) }
                }
        }
    }

    private suspend fun refreshMainProfileCard() {
        userRepository.getMe().onSuccess { fresh ->
            _uiState.update { it.copy(user = fresh) }
        }
    }

    fun changeGoalsChartPeriod(period: String) {
        Storage.save(StorageKeys.CHART_GOALS_PERIOD, period)
        _uiState.update { it.copy(goalsChartPeriod = period) }
        viewModelScope.launch { loadStatsChartIfNeeded(period) }
    }

    fun changeAssistsChartPeriod(period: String) {
        Storage.save(StorageKeys.CHART_ASSISTS_PERIOD, period)
        _uiState.update { it.copy(assistsChartPeriod = period) }
        viewModelScope.launch { loadStatsChartIfNeeded(period) }
    }

    private suspend fun loadStatsChartIfNeeded(period: String) {
        if (_uiState.value.statsCharts.containsKey(period)) return
        val sportId = Storage.get(StorageKeys.SELECTED_SPORT_ID)
        if (sportId.isNullOrBlank()) {
            logger.debug("No sport selected, skipping chart load")
            return
        }
        val chartResult = userRepository.getMyStatsChart(
            mapOf("period" to period, "sportId" to sportId)
        )
        chartResult.onSuccess { chart ->
            _uiState.update { it.copy(statsCharts = it.statsCharts + (period to chart)) }
        }.onFailure { e ->
            logger.debug("Stats chart not available: ${e.message}")
        }
    }

    fun changeTeamChartPeriod(period: String) {
        _uiState.update { it.copy(teamChartPeriod = period, teamChart = null) }
        viewModelScope.launch {
            loadTeamChart(period)
        }
    }

    private suspend fun loadTeamData() {
        if (teamRepository == null) return

        val teamsResult = teamRepository.getMyTeams()
        teamsResult.onSuccess { teams ->
            val firstTeam = teams.firstOrNull() ?: return@onSuccess

            // Load full team with members
            teamRepository.getTeam(firstTeam.id).onSuccess { teamWithMembers ->
                // TEMP: use current user's card avatar for all members
                val myUserId = _uiState.value.user?.id
                val myCardAvatar = teamWithMembers.members
                    .firstOrNull { it.userId == myUserId }?.cardAvatarUrl
                val patchedMembers = if (myCardAvatar != null) {
                    teamWithMembers.members.map { it.copy(cardAvatarUrl = myCardAvatar) }
                } else {
                    teamWithMembers.members
                }
                _uiState.update { it.copy(team = teamWithMembers.copy(members = patchedMembers)) }
            }.onFailure { e ->
                logger.debug("Failed to load team: ${e.message}")
            }

            // Load team matches
            teamRepository.getMatches(firstTeam.id).onSuccess { matches ->
                _uiState.update { it.copy(teamMatches = matches) }
            }.onFailure { e ->
                logger.debug("Failed to load team matches: ${e.message}")
            }

            // Load team chart
            loadTeamChart(_uiState.value.teamChartPeriod, firstTeam.id)
        }.onFailure { e ->
            logger.debug("Failed to load teams: ${e.message}")
        }
    }

    private suspend fun loadTeamChart(period: String, teamId: String? = null) {
        val id = teamId ?: _uiState.value.team?.id
        if (id == null || teamRepository == null) return

        teamRepository.getChart(id, mapOf("period" to period)).onSuccess { chart ->
            _uiState.update { it.copy(teamChart = chart) }
        }.onFailure { e ->
            logger.debug("Failed to load team chart: ${e.message}")
        }
    }

    /** Persist editable Personal Info to the backend via PUT /v1/users/me. */
    fun updatePersonalInfo(
        fullName: String,
        dateOfBirthDisplay: String,
        city: String,
        bio: String,
        languages: List<String>
    ) {
        viewModelScope.launch {
            // Send bio/city as empty strings (not nulls) when cleared — the
            // backend treats `null` as "leave unchanged" (`bio ?: existing`),
            // so `takeIf { it.isNotBlank() }` would make the field
            // un-clearable. fullName keeps the takeIf because we don't want
            // a blank submit to wipe the user's name.
            val request = UpdateUserRequest(
                fullName = fullName.takeIf { it.isNotBlank() },
                bio = bio,
                city = city,
                dateOfBirth = parseDobToIso(dateOfBirthDisplay),
                languages = languages
            )
            userRepository.updateMe(request).onSuccess { updated ->
                _uiState.update { it.copy(user = updated) }
            }.onFailure { e ->
                logger.debug("Failed to update profile: ${e.message}")
                _uiState.update { it.copy(error = e.message) }
            }
        }
    }

    // ── Follow popups ──

    fun openFollowersDialog() {
        _uiState.update { it.copy(showFollowersDialog = true) }
        if (_uiState.value.followers.isEmpty()) reloadFollowers()
    }

    fun openFollowingDialog() {
        _uiState.update { it.copy(showFollowingDialog = true) }
        if (_uiState.value.following.isEmpty()) reloadFollowing()
    }

    fun closeFollowersDialog() {
        _uiState.update { it.copy(showFollowersDialog = false) }
    }

    fun closeFollowingDialog() {
        _uiState.update { it.copy(showFollowingDialog = false) }
    }

    fun requestUnfollow(user: FollowUserDto) {
        _uiState.update { it.copy(unfollowConfirmTarget = user) }
    }

    fun cancelUnfollow() {
        _uiState.update { it.copy(unfollowConfirmTarget = null) }
    }

    fun confirmUnfollow() {
        val target = _uiState.value.unfollowConfirmTarget ?: return
        viewModelScope.launch {
            followRepository?.unfollowUser(target.userId)
                ?.onSuccess {
                    _uiState.update {
                        it.copy(
                            unfollowConfirmTarget = null,
                            unfollowedInSessionIds = it.unfollowedInSessionIds + target.userId,
                            followingCount = ((it.followingCount ?: 0L) - 1).coerceAtLeast(0L)
                        )
                    }
                }
                ?.onFailure { e ->
                    _uiState.update { it.copy(unfollowConfirmTarget = null, error = e.message) }
                }
        }
    }

    /** Re-follow a user who was unfollowed in the current dialog session. */
    fun refollowInDialog(user: FollowUserDto) {
        viewModelScope.launch {
            followRepository?.followUser(user.userId)
                ?.onSuccess {
                    _uiState.update {
                        it.copy(
                            unfollowedInSessionIds = it.unfollowedInSessionIds - user.userId,
                            followingCount = (it.followingCount ?: 0L) + 1
                        )
                    }
                }
                ?.onFailure { e ->
                    _uiState.update { it.copy(error = e.message) }
                }
        }
    }

    fun requestRemoveFollower(user: FollowUserDto) {
        _uiState.update { it.copy(removeFollowerConfirmTarget = user) }
    }

    fun cancelRemoveFollower() {
        _uiState.update { it.copy(removeFollowerConfirmTarget = null) }
    }

    fun confirmRemoveFollower() {
        val target = _uiState.value.removeFollowerConfirmTarget ?: return
        viewModelScope.launch {
            followRepository?.removeFollower(target.userId)
                ?.onSuccess {
                    _uiState.update {
                        it.copy(
                            removeFollowerConfirmTarget = null,
                            deletedFollowerIds = it.deletedFollowerIds + target.userId,
                            followersCount = ((it.followersCount ?: 0L) - 1).coerceAtLeast(0L)
                        )
                    }
                }
                ?.onFailure { e ->
                    _uiState.update { it.copy(removeFollowerConfirmTarget = null, error = e.message) }
                }
        }
    }

    private fun reloadFollowers() {
        viewModelScope.launch {
            _uiState.update { it.copy(followersListLoading = true) }
            followRepository?.getMyFollowers(mapOf("page" to "1", "size" to "100"))
                ?.onSuccess { page ->
                    _uiState.update {
                        it.copy(
                            followers = page.items,
                            followersCount = page.totalItems,
                            followersListLoading = false,
                        )
                    }
                }
                ?.onFailure { e ->
                    _uiState.update { it.copy(followersListLoading = false, error = e.message) }
                }
                ?: _uiState.update { it.copy(followersListLoading = false) }
        }
    }

    private fun reloadFollowing() {
        viewModelScope.launch {
            _uiState.update { it.copy(followingListLoading = true) }
            followRepository?.getMyFollowing(mapOf("page" to "1", "size" to "100"))
                ?.onSuccess { page ->
                    _uiState.update {
                        it.copy(
                            following = page.items,
                            followingCount = page.totalItems,
                            followingListLoading = false,
                        )
                    }
                }
                ?.onFailure { e ->
                    _uiState.update { it.copy(followingListLoading = false, error = e.message) }
                }
                ?: _uiState.update { it.copy(followingListLoading = false) }
        }
    }

    /** Persist Focus & Activities selections to the backend. */
    fun updateInterests(
        sports: Set<String>,
        activities: Set<String>,
        lifestyle: Set<String>,
        notes: Map<String, String>
    ) {
        viewModelScope.launch {
            val request = UpdateUserRequest(
                interestSports = sports.toList(),
                interestActivities = activities.toList(),
                interestLifestyle = lifestyle.toList(),
                interestNotes = notes
            )
            userRepository.updateMe(request).onSuccess { updated ->
                _uiState.update { it.copy(user = updated) }
            }.onFailure { e ->
                logger.debug("Failed to update interests: ${e.message}")
                _uiState.update { it.copy(error = e.message) }
            }
        }
    }
}

/** Convert "DD.MM.YYYY" → "YYYY-MM-DD". Returns null when input is blank or malformed. */
private fun parseDobToIso(display: String): String? {
    val parts = display.trim().split('.', '-', '/')
    if (parts.size != 3) return null
    val (d, m, y) = parts
    if (d.length !in 1..2 || m.length !in 1..2 || y.length != 4) return null
    if (d.toIntOrNull() == null || m.toIntOrNull() == null || y.toIntOrNull() == null) return null
    return "$y-${m.padStart(2, '0')}-${d.padStart(2, '0')}"
}
